import type { CorpusDocumentRow } from "@noa/api-core";
import { EllipsisVertical, ExternalLink, Eye, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  DataTable,
  Dropdown,
  Tag,
  Text,
  Tooltip,
  type DataTableColumn,
  type DropdownItem,
} from "~/components/ui";
import { FOCUS_RING } from "~/components/ui/focus";
import { sourceLabelKey } from "~/utils/corpus-admin";
import { buildDocumentUrl, openDocument } from "~/utils/document-url.utils";

interface CorpusDocumentTableProps {
  documents: CorpusDocumentRow[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  onView: (document: CorpusDocumentRow) => void;
  /** Offered on each row only when given: deleting needs the admin role. */
  onDelete?: (document: CorpusDocumentRow) => void;
  /** A column of chunk counts: what an admin diagnoses, not what a user looks for. */
  showChunks: boolean;
  /** What to show when no row is listed. */
  emptyContent: ReactNode;
}

/** One row per stored document (source URL), paged by the server. */
export function CorpusDocumentTable({
  documents,
  total,
  page,
  pageSize,
  onPageChange,
  loading,
  onView,
  onDelete,
  showChunks,
  emptyContent,
}: CorpusDocumentTableProps) {
  const { t, i18n } = useTranslation("admin");

  const actions = (document: CorpusDocumentRow): DropdownItem[] => {
    // Only a URL the app knows how to open: never a raw stored value handed to the browser
    const sourceUrl = buildDocumentUrl(document.source_url);
    return [
      {
        key: "view",
        icon: <Eye size={14} />,
        label: t("vectorstoreAdmin.actions.view"),
        onClick: () => onView(document),
      },
      ...(sourceUrl
        ? [
            {
              key: "open",
              icon: <ExternalLink size={14} />,
              label: t("vectorstoreAdmin.actions.open"),
              onClick: () => openDocument(sourceUrl),
            },
          ]
        : []),
      ...(onDelete
        ? [
            { type: "divider" as const },
            {
              key: "delete",
              icon: <Trash2 size={14} />,
              label: t("vectorstoreAdmin.actions.delete"),
              danger: true,
              onClick: () => onDelete(document),
            },
          ]
        : []),
    ];
  };

  const chunksColumn: DataTableColumn<CorpusDocumentRow> = {
    key: "chunks",
    title: t("vectorstoreAdmin.columns.chunks"),
    width: 100,
    align: "right",
    render: (document) => document.chunk_count.toLocaleString(i18n.language),
  };

  const columns: DataTableColumn<CorpusDocumentRow>[] = [
    {
      key: "name",
      title: t("vectorstoreAdmin.columns.name"),
      // A long regulation name truncates rather than pushing the actions off screen
      ellipsis: true,
      render: (document) => (
        <button
          type="button"
          onClick={() => onView(document)}
          title={document.source_url}
          className={cn(
            FOCUS_RING,
            "block w-full cursor-pointer truncate rounded border-0 bg-transparent p-0 text-left text-noa underline decoration-noa-muted decoration-dotted underline-offset-4 hover:decoration-solid"
          )}
        >
          {document.name}
        </button>
      ),
    },
    {
      key: "types",
      title: t("vectorstoreAdmin.columns.types"),
      width: "22%",
      render: (document) => {
        // Chunks without a record: the assistant retrieves them, but no type lists them
        if (showChunks && document.records === 0) {
          return (
            <Tooltip title={t("vectorstoreAdmin.orphan.hint")}>
              <Tag color="warning">{t("vectorstoreAdmin.orphan.tag")}</Tag>
            </Tooltip>
          );
        }
        return document.document_types.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {document.document_types.map((type) => (
              <Tag key={type}>{type}</Tag>
            ))}
          </div>
        ) : (
          <Text tone="muted">{t("vectorstoreAdmin.overview.untyped")}</Text>
        );
      },
    },
    {
      key: "source",
      title: t("vectorstoreAdmin.columns.source"),
      width: 130,
      render: (document) =>
        document.source_systems.map((source) => t(sourceLabelKey(source), { defaultValue: source })).join(", "),
    },
    ...(showChunks ? [chunksColumn] : []),
    {
      key: "ingestedAt",
      title: t("vectorstoreAdmin.columns.ingestedAt"),
      width: 120,
      render: (document) =>
        document.ingested_at ? new Date(document.ingested_at).toLocaleDateString(i18n.language) : "—",
    },
    {
      key: "actions",
      title: t("vectorstoreAdmin.columns.actions"),
      width: 80,
      align: "center",
      render: (document) => (
        <Dropdown menu={{ items: actions(document) }} trigger={["click"]}>
          <Button
            variant="text"
            icon={<EllipsisVertical size={14} />}
            aria-label={t("vectorstoreAdmin.actions.moreFor", { name: document.name })}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={documents}
      rowKey={(document) => document.source_url}
      loading={loading}
      pagination={{ pageSize, current: page, total, onChange: onPageChange, hideOnSinglePage: true }}
      emptyContent={emptyContent}
    />
  );
}
